import React, { useState } from 'react';
import {
  X,
  CreditCard,
  Landmark,
  Receipt,
  Zap,
  Briefcase,
  Gift,
  ShoppingBag,
  Sparkles,
  ArrowRight,
  Shield,
  Clock,
  Search,
  Check,
} from 'lucide-react';

export const CARD_TEMPLATES = [
  {
    id: 'credit',
    title: 'Credit Card',
    cardType: 'credit',
    category: 'Finance',
    tags: ['rewards', 'travel', 'credit-card'],
    icon: CreditCard,
    color: '#3b82f6',
    theme: 'sapphire',
    description: 'Travel, cash-back, or premium rewards credit card with interest cycle and credit limit tracking.',
    defaultName: 'Chase Sapphire Preferred',
    fields: {
      cardName: 'Chase Sapphire Preferred',
      cardType: 'credit',
      issuer: 'Chase Bank',
      cardholderName: 'Nikhil Sharma',
      cardNumber: '4111 8294 1029 4820',
      expiryMonth: '08',
      expiryYear: '2029',
      cvv: '849',
      pin: '',
      currency: 'USD',
      billingAddress: {
        street: '742 Evergreen Terrace',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      notes: '3x points on dining, 2x on travel. Zero foreign exchange fees. Customer support: 1-800-432-3117.',
    },
  },
  {
    id: 'debit',
    title: 'Debit Card',
    cardType: 'debit',
    category: 'Personal',
    tags: ['debit', 'checking', 'atm'],
    icon: Landmark,
    color: '#10b981',
    theme: 'emerald',
    description: 'Bank checking account debit card for daily retail purchases, POS payments, and global ATM cash withdrawals.',
    defaultName: 'BofA Advantage Debit',
    fields: {
      cardName: 'Bank of America Checking Debit',
      cardType: 'debit',
      issuer: 'Bank of America',
      cardholderName: 'Nikhil Sharma',
      cardNumber: '4792 1029 3847 1948',
      expiryMonth: '11',
      expiryYear: '2028',
      cvv: '392',
      pin: '7741',
      currency: 'USD',
      accountNumber: '918273645019',
      ifscCode: 'BOFA0US33SF',
      billingAddress: {
        street: '742 Evergreen Terrace',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      notes: 'Linked to primary checking account. Daily ATM withdrawal limit: $1,000.',
    },
  },
  {
    id: 'prepaid',
    title: 'Prepaid Card',
    cardType: 'prepaid',
    category: 'Travel',
    tags: ['prepaid', 'travel', 'reloadable'],
    icon: Receipt,
    color: '#f97316',
    theme: 'sunset',
    description: 'Reloadable prepaid debit card for budgeting, vacation travel, or shared family allowances.',
    defaultName: 'Revolut Multi-Currency Travel',
    fields: {
      cardName: 'Revolut Global Travel Card',
      cardType: 'prepaid',
      issuer: 'Revolut',
      cardholderName: 'Nikhil Sharma',
      cardNumber: '5241 9482 1029 3840',
      expiryMonth: '05',
      expiryYear: '2027',
      cvv: '412',
      pin: '1920',
      currency: 'EUR',
      billingAddress: {
        street: '742 Evergreen Terrace',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      notes: 'Multi-currency account holding USD, EUR, and GBP balances. Auto-freeze enabled via mobile app.',
    },
  },
  {
    id: 'virtual',
    title: 'Virtual Card',
    cardType: 'virtual',
    category: 'Online Subscriptions',
    tags: ['virtual-card', 'subscriptions', 'burner'],
    icon: Zap,
    color: '#8b5cf6',
    theme: 'cyber',
    description: 'Single-merchant or disposable virtual payment card designed for SaaS tools and secure online subscriptions.',
    defaultName: 'Privacy SaaS Virtual Card',
    fields: {
      cardName: 'Cloud SaaS Subscriptions',
      cardType: 'virtual',
      issuer: 'Privacy.com / Stripe',
      cardholderName: 'Nikhil Sharma',
      cardNumber: '4000 1234 5678 9010',
      expiryMonth: '12',
      expiryYear: '2026',
      cvv: '921',
      pin: '',
      currency: 'USD',
      billingAddress: {
        street: '742 Evergreen Terrace',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      notes: 'Locked to AWS and GitHub billing. Monthly spend limit capped at $150.',
    },
  },
  {
    id: 'business',
    title: 'Business Card',
    cardType: 'business',
    category: 'Business',
    tags: ['corporate', 'business-expenses', 'amex'],
    icon: Briefcase,
    color: '#eab308',
    theme: 'gold',
    description: 'Corporate corporate charge or credit card for company procurement, travel booking, and employee expenses.',
    defaultName: 'Amex Business Platinum',
    fields: {
      cardName: 'American Express Business Platinum',
      cardType: 'business',
      issuer: 'American Express',
      cardholderName: 'Nikhil Sharma',
      cardNumber: '3782 822456 91002',
      expiryMonth: '09',
      expiryYear: '2028',
      cvv: '4821',
      pin: '',
      currency: 'USD',
      billingAddress: {
        street: '500 Howard Street, Suite 800',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94105',
        country: 'United States',
      },
      notes: 'Corporate Card. Upload all flight and hotel receipts for end-of-month expensing.',
    },
  },
  {
    id: 'gift',
    title: 'Gift Card',
    cardType: 'gift',
    category: 'Shopping',
    tags: ['gift-card', 'retail', 'amazon'],
    icon: Gift,
    color: '#ec4899',
    theme: 'onyx',
    description: 'Retail store voucher, merchant digital gift balance, or prepaid restaurant gift token.',
    defaultName: 'Amazon E-Gift Balance',
    fields: {
      cardName: 'Amazon Digital Gift Card',
      cardType: 'gift',
      issuer: 'Amazon',
      cardholderName: 'Gift Recipient',
      cardNumber: '6014 9102 3847 1029',
      expiryMonth: '12',
      expiryYear: '2035',
      cvv: '000',
      pin: '8492',
      currency: 'USD',
      billingAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'United States',
      },
      notes: 'Redeem at amazon.com/redeem. $250 promotional credit balance.',
    },
  },
  {
    id: 'store',
    title: 'Store / Retail Card',
    cardType: 'store',
    category: 'Shopping',
    tags: ['retail', 'store-card', 'cashback'],
    icon: ShoppingBag,
    color: '#06b6d4',
    theme: 'platinum',
    description: 'Department store or merchant credit card offering loyalty cash-back and retail financing terms.',
    defaultName: 'Apple Titanium Card',
    fields: {
      cardName: 'Apple Card Goldman Sachs',
      cardType: 'store',
      issuer: 'Goldman Sachs Bank',
      cardholderName: 'Nikhil Sharma',
      cardNumber: '5424 1029 4810 5928',
      expiryMonth: '04',
      expiryYear: '2029',
      cvv: '582',
      pin: '',
      currency: 'USD',
      billingAddress: {
        street: '742 Evergreen Terrace',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      notes: '3% Daily Cash back on Apple purchases. Physical titanium laser-etched card.',
    },
  },
  {
    id: 'custom',
    title: 'Custom Card',
    cardType: 'custom',
    category: 'Custom',
    tags: ['custom-card', 'membership'],
    icon: Sparkles,
    color: '#a855f7',
    theme: 'violet',
    description: 'Customizable card template for private club cards, transit passes, or specialized banking cards.',
    defaultName: 'Executive Club Membership Card',
    fields: {
      cardName: 'Executive Club Card',
      cardType: 'custom',
      issuer: 'Global Concierge',
      cardholderName: 'Nikhil Sharma',
      cardNumber: '4920 1029 3847 2810',
      expiryMonth: '01',
      expiryYear: '2030',
      cvv: '123',
      pin: '4321',
      currency: 'USD',
      billingAddress: {
        street: '742 Evergreen Terrace',
        city: 'San Francisco',
        state: 'CA',
        postalCode: '94107',
        country: 'United States',
      },
      notes: 'VIP Lounge access and concierge services.',
    },
  },
];

export default function CardTemplatesModal({ open, onClose, onSelectTemplate }) {
  const [selectedId, setSelectedId] = useState(CARD_TEMPLATES[0].id);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  if (!open) return null;

  const categories = ['All', 'Finance', 'Personal', 'Travel', 'Online Subscriptions', 'Store', 'Custom'];

  const filteredTemplates = CARD_TEMPLATES.filter((tpl) => {
    const matchesCategory =
      activeCategory === 'All' ||
      tpl.category.toLowerCase() === activeCategory.toLowerCase();

    const q = searchQuery.trim().toLowerCase();
    if (!q) return matchesCategory;

    return (
      matchesCategory &&
      (tpl.title.toLowerCase().includes(q) ||
        tpl.description.toLowerCase().includes(q) ||
        tpl.defaultName.toLowerCase().includes(q) ||
        tpl.category.toLowerCase().includes(q) ||
        tpl.tags.some((t) => t.toLowerCase().includes(q)))
    );
  });

  const activeTemplate =
    CARD_TEMPLATES.find((t) => t.id === selectedId) ||
    filteredTemplates[0] ||
    CARD_TEMPLATES[0];
  const IconComp = activeTemplate.icon;

  return (
    <div className="card-drawer-wrapper">
      <div
        className="card-drawer-overlay animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="card-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Reusable Card Templates"
      >
        {/* Drawer Header */}
        <div className="card-drawer-header">
          <div className="card-drawer-header-left">
            <div
              className="card-drawer-icon-badge"
              style={{ background: 'rgba(139, 92, 246, 0.14)', color: '#8b5cf6' }}
            >
              <Sparkles size={20} />
            </div>
            <div className="card-drawer-titles">
              <h2 className="card-drawer-title">Card Templates</h2>
              <p className="card-drawer-subtitle">
                Choose from 8 pre-configured payment presets
              </p>
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <X size={18} />
          </button>
        </div>

        {/* Drawer Body */}
        <div className="card-drawer-body">
          {/* Search bar */}
          <div className="admin-search-box" style={{ width: '100%', marginBottom: '10px', height: '36px', boxSizing: 'border-box' }}>
            <Search size={14} color="var(--text-secondary)" />
            <input
              type="text"
              placeholder="Search templates (e.g. Travel, Debit, Amex)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
              aria-label="Search card templates"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 0 }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Category Filter Pills */}
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '12px', scrollbarWidth: 'none' }}>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`card-segmented-btn ${activeCategory === cat ? 'active' : ''}`}
                style={{
                  padding: '4px 10px',
                  borderRadius: '16px',
                  fontSize: '0.72rem',
                  border: activeCategory === cat ? '1px solid var(--accent-primary)' : '1px solid var(--border-color)',
                  background: activeCategory === cat ? 'var(--accent-primary)' : 'var(--bg-surface)',
                  color: activeCategory === cat ? '#ffffff' : 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Selected Template Live Preview Card */}
          {activeTemplate && (
            <div className="card-ios-group" style={{ padding: '14px', marginBottom: '16px', background: 'var(--bg-surface)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      background: `${activeTemplate.color}18`,
                      color: activeTemplate.color,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <IconComp size={14} />
                  </div>
                  <strong style={{ fontSize: '0.88rem' }}>{activeTemplate.defaultName}</strong>
                </div>
                <span className="badge badge-secondary" style={{ fontSize: '0.68rem', padding: '1px 6px' }}>
                  {activeTemplate.category}
                </span>
              </div>

              {/* Digital Card Preview */}
              <div className={`digital-card-hero theme-${activeTemplate.theme || 'sapphire'}`} style={{ maxWidth: '300px', margin: '0 auto 12px auto' }}>
                <div className="card-hero-header">
                  <span className="card-hero-issuer" style={{ fontSize: '0.82rem' }}>{activeTemplate.fields.issuer}</span>
                  <span className="card-hero-network-badge" style={{ fontSize: '0.7rem', padding: '2px 8px' }}>
                    {activeTemplate.cardType.toUpperCase()}
                  </span>
                </div>
                <div className="card-hero-chip-row">
                  <div className="card-hero-emv-chip" style={{ width: '32px', height: '22px' }}></div>
                  <div className="card-hero-contactless">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <div className="card-hero-number" style={{ fontSize: '1.05rem' }}>
                  •••• •••• •••• {activeTemplate.fields.cardNumber.slice(-4)}
                </div>
                <div className="card-hero-footer">
                  <div>
                    <span className="card-hero-sublabel" style={{ fontSize: '0.55rem' }}>CARDHOLDER</span>
                    <span className="card-hero-holder-name" style={{ fontSize: '0.72rem' }}>
                      {activeTemplate.fields.cardholderName}
                    </span>
                  </div>
                  <div>
                    <span className="card-hero-sublabel" style={{ fontSize: '0.55rem' }}>EXPIRES</span>
                    <span className="card-hero-exp-date" style={{ fontSize: '0.72rem' }}>
                      {activeTemplate.fields.expiryMonth}/{activeTemplate.fields.expiryYear.slice(-2)}
                    </span>
                  </div>
                </div>
              </div>

              <p style={{ margin: '0 0 10px 0', fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                {activeTemplate.description}
              </p>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ width: '100%', justifyContent: 'center', fontSize: '0.8rem', padding: '7px 12px' }}
                onClick={() => onSelectTemplate(activeTemplate)}
              >
                <span>Use {activeTemplate.title} Template</span>
                <ArrowRight size={14} />
              </button>
            </div>
          )}

          {/* List of All Templates */}
          <div style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '8px' }}>
            All Preset Templates ({filteredTemplates.length})
          </div>

          <div className="card-templates-drawer-list">
            {filteredTemplates.map((tmpl) => {
              const TmplIcon = tmpl.icon;
              const isSelected = tmpl.id === activeTemplate?.id;
              return (
                <div
                  key={tmpl.id}
                  className={`card-template-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedId(tmpl.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSelectedId(tmpl.id); }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: `${tmpl.color}16`,
                      color: tmpl.color,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <TmplIcon size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                      <strong style={{ fontSize: '0.84rem', color: 'var(--text-primary)' }}>{tmpl.title}</strong>
                      <span className="badge badge-secondary" style={{ fontSize: '0.66rem', padding: '1px 6px' }}>
                        {tmpl.category}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {tmpl.defaultName} • {tmpl.description}
                    </div>
                  </div>
                  {isSelected && (
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--accent-primary)', color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                      <Check size={12} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Drawer Sticky Footer */}
        <div className="card-drawer-footer">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '0.8rem', padding: '7px 14px' }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ fontSize: '0.8rem', padding: '7px 16px' }}
            onClick={() => onSelectTemplate(activeTemplate)}
          >
            <span>Use Selected Template</span>
            <ArrowRight size={14} />
          </button>
        </div>
      </aside>
    </div>
  );
}
